import { useState } from 'react'
import { useMutation } from 'urql'
import { gql } from '../gql/gql'
import { Button } from './ui/button'
import { Input } from './ui/input'
import { Card, CardContent } from './ui/card'
import { Pencil, Target } from 'lucide-react'
import { cn } from '@/lib/utils'

const UPDATE_BUDGET_MUTATION = gql(`
  mutation UpdateBudget($input: UpdateUserInput!) {
    updateUser(input: $input) {
      id
      monthlyBudget
    }
  }
`)

interface BudgetPace {
  message: string
  onTrack: boolean
}

interface BudgetCardProps {
  budget: number | null | undefined
  spent: number
  currencySymbol: string
  chartView: 'monthly' | 'yearly'
  chartDate: Date
  budgetPace?: BudgetPace | null
  onSuccess: () => void
  /** When true, renders without the outer Card wrapper (for embedding inside another card) */
  embedded?: boolean
}

export function BudgetCard({ budget, spent, currencySymbol, chartView, chartDate, budgetPace, onSuccess, embedded }: BudgetCardProps) {
  const [editing, setEditing] = useState(false)
  const [inputValue, setInputValue] = useState('')
  const [{ fetching }, updateUser] = useMutation(UPDATE_BUDGET_MUTATION)

  const displayBudget = chartView === 'yearly' && budget ? budget * 12 : budget

  const today = new Date()
  const isCurrentMonth =
    chartView === 'monthly' &&
    chartDate.getMonth() === today.getMonth() &&
    chartDate.getFullYear() === today.getFullYear()
  const isPastMonth = chartView === 'monthly' && !isCurrentMonth

  const budgetLabel = chartView === 'yearly'
    ? `${chartDate.getFullYear()} budget`
    : isCurrentMonth
      ? 'monthly budget'
      : `${chartDate.toLocaleString('default', { month: 'short' })} budget`

  const paceMessage = isCurrentMonth ? (budgetPace?.message ?? null) : null
  const paceOnTrack = budgetPace?.onTrack ?? true

  const percentage = displayBudget && displayBudget > 0 ? Math.min(100, (spent / displayBudget) * 100) : 0
  const isOverBudget = !!displayBudget && spent > displayBudget
  const isAmber = percentage >= 80 && !isOverBudget
  const remaining = displayBudget ? displayBudget - spent : 0

  const handleSave = async () => {
    const val = inputValue ? parseFloat(inputValue) : null
    if (val === null) { setEditing(false); return }
    await updateUser({ input: { monthlyBudget: val } })
    setEditing(false)
    onSuccess()
  }

  const handleClear = async () => {
    await updateUser({ input: { clearMonthlyBudget: true } })
    setEditing(false)
    onSuccess()
  }

  const startEditing = () => {
    setInputValue(budget?.toString() || '')
    setEditing(true)
  }

  // Empty state
  if (!displayBudget || displayBudget === 0) {
    const content = editing ? (
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-sm text-muted-foreground whitespace-nowrap">Monthly budget</span>
        <div className="flex items-center gap-1">
          <span className="text-sm text-muted-foreground">{currencySymbol}</span>
          <Input
            type="number"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') handleSave(); if (e.key === 'Escape') setEditing(false) }}
            placeholder="e.g. 2000"
            className="h-7 w-28"
            autoFocus
          />
        </div>
        <Button size="sm" onClick={handleSave} disabled={fetching}>
          {fetching ? 'Saving…' : 'Save'}
        </Button>
        <Button size="sm" variant="ghost" onClick={() => setEditing(false)}>Cancel</Button>
      </div>
    ) : (
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-muted-foreground">
          <Target className="h-3.5 w-3.5" />
          <span className="text-sm">No {budgetLabel} set</span>
        </div>
        <Button size="sm" variant="ghost" onClick={startEditing} className="text-sm h-7">
          + Set a monthly budget
        </Button>
      </div>
    )

    if (embedded) return <div className="w-full">{content}</div>
    return <Card className="mb-4"><CardContent className="p-3">{content}</CardContent></Card>
  }

  const content = editing ? (
    <div className="flex items-center gap-2 flex-wrap">
      <span className="text-sm text-muted-foreground whitespace-nowrap">Monthly budget</span>
      <div className="flex items-center gap-1">
        <span className="text-sm text-muted-foreground">{currencySymbol}</span>
        <Input
          type="number"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') handleSave(); if (e.key === 'Escape') setEditing(false) }}
          className="h-7 w-28"
          autoFocus
        />
      </div>
      <Button size="sm" onClick={handleSave} disabled={fetching}>
        {fetching ? 'Saving…' : 'Save'}
      </Button>
      <Button size="sm" variant="ghost" onClick={() => setEditing(false)}>Cancel</Button>
      <Button size="sm" variant="ghost" onClick={handleClear} disabled={fetching} className="text-muted-foreground hover:text-destructive">
        Clear
      </Button>
    </div>
  ) : (
    <>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-baseline gap-1.5">
          <span className={cn('text-base font-semibold tabular-nums', isOverBudget && 'text-destructive')}>
            {remaining >= 0
              ? `${currencySymbol}${remaining.toFixed(0)}`
              : `-${currencySymbol}${Math.abs(remaining).toFixed(0)}`}
          </span>
          <span className="text-xs text-muted-foreground">
            {remaining >= 0 ? (isPastMonth ? 'under budget' : 'remaining') : 'over budget'}
          </span>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="text-xs text-muted-foreground">
            {currencySymbol}{spent.toFixed(0)} / {currencySymbol}{displayBudget.toFixed(0)} {budgetLabel}
          </span>
          <Button variant="ghost" size="icon" className="h-6 w-6" onClick={startEditing}>
            <Pencil className="h-3 w-3" />
          </Button>
        </div>
      </div>

      <div className="h-1.5 w-full bg-secondary rounded-full overflow-hidden">
        <div
          className={cn(
            'h-full transition-all duration-500 rounded-full',
            isOverBudget ? 'bg-destructive' : isAmber ? 'bg-amber-500' : 'bg-primary'
          )}
          style={{ width: `${percentage}%` }}
        />
      </div>

      {paceMessage && (
        <p className={cn('text-xs mt-1', paceOnTrack ? 'text-muted-foreground' : 'text-amber-600 font-medium')}>
          {paceOnTrack ? '✓' : '⚠'} {paceMessage}
        </p>
      )}
    </>
  )

  if (embedded) return <div className="w-full">{content}</div>
  return <Card className="mb-4"><CardContent className="p-3">{content}</CardContent></Card>
}
